export enum TextWritingType {
    /** 水平-从左到右 */
    HORIZONTAL_TB = 'horizontal-tb',
    /** 垂直-从左到右 */
    VERTICAL_LR = 'vertical-lr',
    /** 垂直-从右到左 */
    VERTICAL_RL = 'vertical-rl',
}

export enum TextAlignType {
    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
    JUSTIFY = 'justify',
}

export enum TextVerticalAlign {
    TOP = 'top',
    CENTER = 'center',
    BOTTOM = 'bottom',
}

/** 描边方式 */
export enum StrokeDirectionType {
    WITHOUT = 0,
    CENTER = 1,
}

/** 发光 */
export interface IFontGlow {
    id?: string;
    color: string;
    blur?: number;
    width: number;
    hidden?: boolean;
    // 是否可编辑
    editable?: boolean;
}

/** 阴影 */
export interface IFontShadow {
    id?: string;
    color: string;
    x: number;
    y: number;
    // 半径
    blur: number;
    hidden?: boolean;
    // 是否可编辑
    editable?: boolean;
}

/** 描边 */
export interface IFontStroke {
    id?: string;
    color: string;
    width: number;
    /** 描边方式 */
    direction: StrokeDirectionType;
    hidden?: boolean;
    // 是否可编辑
    editable?: boolean;
}

// 纹理填充
export interface ITexture {
    name?: string;
    url: string;
    stretch: string;
    scale: number;
}

export interface IFontBackground {
    color: string;
    hidden?: boolean;
    // opacity?: number;
    // 是否可编辑
    editable?: boolean;
    /** 圆角 */
    roundWeight?: number;
}

export interface IFontTextGradient {
    colors: any[];
    angle: number;
}

export interface GradientColorResult {
    colors: {
        color: string;
        value: number;
    }[];
    angle: number;
}

/** 分段编辑 */
export interface ITextContent {
    color: string;
    fontSize: number;
    fontFamily: string;
    bold: boolean;
    italic: boolean;
    lineThrough: boolean;
    underline: boolean;
    text: string;
    fontFamilySubset?: string;
    textMark?: ITextMark | null;
    textBackground?: IFontBackground[];
    textStroke?: IFontStroke[];
    textShadow?: IFontShadow[];
    textGlow?: IFontGlow[];
    rotate?: number;
    /** 0.8, 1, 1.5,  transform-origin 始终为 50%,50% */
    scale?: number;
    /** 0～100 ， X轴偏移，代表偏移字体大小的百分多少 */
    offsetX?: number;
    /** 0～100 ， Y轴偏移，代表偏移字体大小的百分多少 */
    offsetY?: number;
}

/** 划重点 */
export interface ITextMark {
    materialId: string;
    color: string;
    size: number;
    enableSize: boolean;
}

export type ListStyle = 'decimal' | 'lower-latin' | 'upper-roman' | 'disc' | 'circle' | 'square' | 'custom';

/**
 * 列表文字资源（资源型 listMarker）
 */
export interface IListStyleMaterial {
    id: string;
    autoIncrement: 'none' | Extract<ListStyle, 'decimal' | 'lower-latin' | 'upper-roman'>;
    url: string;
    fontId?: string;
    padZero?: boolean;
    fontColor: string;
    fontSize: number;
    baseContentTextSize: number;
    scale: number;
    itemSpacing?: number;
    iconWidth: number;
    iconHeight: number;
    iconColors: Array<{ color: string; values: number[]; editable?: boolean }>;
}

export interface IAdvancedTextConfig {
    src: string; // svg 路径
    defaultFontSize: number; // 当前svg大小匹配的文字大小
    defaultLetterSpacing: number; // 当前svg大小匹配的字间距
    useCount?: number; // 用来表示按照几个单元来重复
}

export interface IAdvancedTextMaterial {
    id: string;
    textContents: Omit<ITextContent, 'text'>[];
    material: IAdvancedTextConfig;
}

export interface ITextLayerProps {
    /** 文本文字 */
    text: string;
    /** 文本颜色 */
    color: string;
    /** 文本大小 */
    fontSize: number;
    /** 文本字体族 */
    fontFamily: string;
    /** 行间距 */
    lineSpacing: number;
    /** 字间距 */
    letterSpacing: number;
    /** 粗体 */
    bold: boolean;
    /** 斜体 */
    italic: boolean;
    /** 中划线 */
    lineThrough: boolean;
    /** 下划线 */
    underline: boolean;
    /** 对齐放缩 */
    textAlign: TextAlignType;
    /** 文本方向 */
    writingMode: TextWritingType;
    /** 荧光 */
    textGlow: IFontGlow[];
    /** 阴影 */
    textShadow: IFontShadow[];
    /** 描边 */
    textStroke: IFontStroke[];
    /** 纹理填充 */
    texture?: ITexture | null;
    /** 文本背景色 */
    textBackground: IFontBackground[];
    /** 文本渐变 | 背景*/
    textGradient: GradientColorResult | null;
    /** 列表格式 */
    listStyle: ListStyle | '';
    /** 列表素材资源 */
    listStyleMaterial?: IListStyleMaterial;
    /** 分段编辑数据 */
    textContents?: ITextContent[];
    /** 文字形状拓展 */
    shapeExtension?: IShapeExtension | null;
    /** 文字拓展，结果图 */
    screenshotUrl?: string | null;
    /** 文字图层内容是否修改过 */
    modified?: boolean;
    /** 文字划重点 */
    textMark?: ITextMark | null;

    /**
     * 垂直排版（runtime only）
     * 目前只用于（由矢量图层）派生的文本图层
     */
    verticalAlign?: TextVerticalAlign;

    /** 花字素材的配置，当使用V4.0.0花字时，需要将花字素材信息 advancedTextMaterial 拷贝一份到这里 */
    advancedTextMaterial?: IAdvancedTextMaterial;
}

export type TextEffectKind = Extract<
    keyof ITextLayerProps,
    'textStroke' | 'textGlow' | 'textShadow' | 'textBackground'
>;

/**
 * shape text 的形变分类
 */
export enum TextShapeType {
    /**
     * *********
     * 路径变形
     * *********
     */

    curve = 1, // 曲线
    ellipse = 2, // 椭圆
    square = 3, // 方形
    polygon = 4, // 多边形
    triangle = 5, // 三角形
    heart = 6, // 爱心
    rectangle = 7, // 圆角矩形
    tilde = 8, // 波浪线

    /**
     * *********
     * 形状变形
     * *********
     */

    waveShaped = 9, // 波浪形
    bowShaped = 10, // 弓形
    curved = 11, // 弧形
    hexagon = 12, // 六边形
    upperTrapezoid = 13, // 上侧梯形
    upperArc = 14, // 上弧形
    trapezoid = 15, // 梯形
    convexShape = 16, // 凸形
    lowerTrapezoid = 17, // 下侧梯形
    lowerArc = 18, // 下弧形
}

/**
 * 文字效果拓展
 * 参数文档
 * https://cf.meitu.com/confluence/pages/viewpage.action?pageId=407536744
 */
export interface IShapeExtension {
    /** 类型 */
    curveTextType: TextShapeType;
    /** 参数，值为底层参数值 */
    /** 强度 [-360, 360] */
    bendAngle: number;
    /** 大小 [0, 100] */
    spacing: number;
    /** 起始角度, 起始位置 [-1, 1] */
    positionOffset: number;
    /** 长度 [-1, 1] */
    firstMarginRatio: number;
    /** 离心率, 边数, 弧度, 强度 [0, 1] */
    progress: number;
    /** 反转路径 */
    reverse: boolean;
    /** 长宽比 [-1, 1] */
    aspectRatio: number;

    /** 相对高度[0,1]  */
    relativeHeight?: number;
}

/**
 * 文字可编辑图层通用文字基础信息
 *
 * - 基础信息：即不包含特效、变形等装饰性属性
 */
export type BaseTextInfo = {
    text: string;
    color: string;
    fontSize: number;
    fontFamily: string;
    lineSpacing: number;
    letterSpacing: number;
    bold: boolean;
    italic: boolean;
    lineThrough: boolean;
    underline: boolean;
    textAlign: TextAlignType;
    writingMode: TextWritingType;
    opacity: number;
    listStyle?: ListStyle | '';
    listStyleMaterial?: IListStyleMaterial;
    textContents?: ITextContent[];
    verticalAlign?: TextVerticalAlign;
};

type ListStyleBase = {
    id: string;
};

type CharListStyle = ListStyleBase & {
    type: 'char';
    style: Exclude<ListStyle, 'custom'>;
};

type GraphListStyle = ListStyleBase & {
    type: 'image' | 'svg';
    style: 'custom';
    config: IListStyleMaterial;
};

export type ListStyleStruct = CharListStyle | GraphListStyle;
